import { FormEvent, useState } from "react";
import { Mail, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "../api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import type { AuthMeDTO } from "@tendvia/shared";

type AuthScreenProps = {
  onAuthed: (me: AuthMeDTO) => void;
};

export function AuthScreen({ onAuthed }: AuthScreenProps) {
  const [step, setStep] = useState<"request" | "verify">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function requestOtp(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const response = await api.startOtp(email);
      setStep("verify");
      setCode(response.devCode ?? "");
      setMessage(response.devCode ? `Local dev code: ${response.devCode}` : "Check your email for a 6-digit code.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to start login.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      onAuthed(await api.verifyOtp(email, code));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to verify login.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-background px-4 py-8 text-text">
      <section className="w-full max-w-md overflow-hidden rounded-panel border border-border/60 bg-surface/95 p-6 shadow-soft">
        <div className="mb-6 grid gap-5">
          <div className="flex items-center justify-between gap-3">
            <div className="inline-grid size-12 place-items-center rounded-token border border-border/40 bg-surface-soft text-primary">
              <Sparkles className="size-5" aria-hidden="true" />
            </div>
            <span className="rounded-pill border border-border/60 bg-surface-soft px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-primary">
              Field OS
            </span>
          </div>

          <div>
            <p className="text-sm font-extrabold uppercase tracking-[0.18em] text-accent">TendVia</p>
            <h1 className="mt-3 text-[2rem] font-black leading-tight tracking-[-0.045em] text-text">
              Run the day from the same screen you close the job.
            </h1>
            <p className="mt-3 max-w-sm text-sm leading-6 text-muted">
              Quotes, route context, booked work, and follow-up stay in one calm operational flow.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <SignalPill label="Quote" />
            <SignalPill label="Route" />
            <SignalPill label="Follow-up" />
          </div>
        </div>

        {step === "request" ? (
          <form className="grid gap-4" onSubmit={requestOtp}>
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
            <Button icon={<Mail className="size-4" aria-hidden="true" />} disabled={busy || !email}>
              {busy ? "Sending" : "Send login code"}
            </Button>
          </form>
        ) : (
          <form className="grid gap-4" onSubmit={verifyOtp}>
            <Field
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
              <Button icon={<ShieldCheck className="size-4" aria-hidden="true" />} disabled={busy || code.length !== 6}>
                {busy ? "Checking" : "Verify"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setStep("request")}>Use another email</Button>
            </div>
          </form>
        )}

        <div className="mt-5 rounded-token border border-border/55 bg-surface-soft px-4 py-3">
          <p className="text-sm font-extrabold text-text">Passwordless sign-in</p>
          <p className="mt-1 text-sm leading-6 text-muted">We send a 6-digit code to your email so the app stays quick to open between jobs.</p>
        </div>

        {message ? <p className="mt-4 rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{message}</p> : null}
      </section>
    </main>
  );
}

function SignalPill({ label }: { label: string }) {
  return (
    <span className="inline-flex min-h-11 items-center justify-center rounded-token border border-border/55 bg-surface-soft px-3 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-text">
      {label}
    </span>
  );
}
